import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X, Check } from "lucide-react";

const CUR = { INR: "₹", USD: "$" };

function PlanEditor({ category, plan, onClose, onSaved }) {
  const [d, setD] = useState({
    name: plan?.name || "New Plan",
    base_price: plan?.base_price ?? 0,
    currency: plan?.currency || "INR",
    discount_type: plan?.discount_type || "none",
    discount_value: plan?.discount_value ?? 0,
    billing_interval: plan?.billing_interval || "annual",
    trial_days: plan?.trial_days ?? 0,
    features: plan?.features ? [...plan.features] : [],
    is_active: plan?.is_active ?? true,
  });
  const [feat, setFeat] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...d, base_price: Number(d.base_price), discount_value: Number(d.discount_value), trial_days: Number(d.trial_days) };
      if (plan?.id) await api.patch(`/membership/admin/plans/${category}/${plan.id}`, payload);
      else await api.post(`/membership/admin/plans/${category}`, payload);
      toast.success("Plan saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="nb-card p-6 bg-white max-w-md w-full space-y-2 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="membership-editor">
        <div className="flex items-center"><h3 className="display text-xl font-bold">{plan?.id ? "Edit" : "Add"} Plan</h3>
          <button className="ml-auto" onClick={onClose}><X size={18} /></button></div>
        <input className="nb-input" placeholder="Plan name" value={d.name} onChange={e => setD({ ...d, name: e.target.value })} data-testid="membership-name" />
        <div className="grid grid-cols-2 gap-2">
          <label className="block"><span className="nb-label text-[10px]">Base Price</span>
            <input type="number" className="nb-input mt-1" value={d.base_price} onChange={e => setD({ ...d, base_price: e.target.value })} data-testid="membership-price" /></label>
          <label className="block"><span className="nb-label text-[10px]">Currency</span>
            <select className="nb-select mt-1" value={d.currency} onChange={e => setD({ ...d, currency: e.target.value })}>
              <option value="INR">INR ₹</option><option value="USD">USD $</option></select></label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block"><span className="nb-label text-[10px]">Discount Type</span>
            <select className="nb-select mt-1" value={d.discount_type} onChange={e => setD({ ...d, discount_type: e.target.value })} data-testid="membership-discount-type">
              <option value="none">None</option><option value="percent">Percent %</option><option value="flat">Flat</option></select></label>
          <label className="block"><span className="nb-label text-[10px]">Discount Value</span>
            <input type="number" className="nb-input mt-1" value={d.discount_value} disabled={d.discount_type === "none"} onChange={e => setD({ ...d, discount_value: e.target.value })} data-testid="membership-discount-value" /></label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block"><span className="nb-label text-[10px]">Billing Interval</span>
            <select className="nb-select mt-1" value={d.billing_interval} onChange={e => setD({ ...d, billing_interval: e.target.value })} data-testid="membership-interval">
              <option value="annual">Annual</option><option value="monthly">Monthly</option></select></label>
          <label className="block"><span className="nb-label text-[10px]">Trial (days)</span>
            <input type="number" className="nb-input mt-1" value={d.trial_days} onChange={e => setD({ ...d, trial_days: e.target.value })} data-testid="membership-trial" /></label>
        </div>
        <div>
          <span className="nb-label text-[10px]">Feature Checklist</span>
          <div className="flex gap-2 mt-1">
            <input className="nb-input flex-1" placeholder="Add feature…" value={feat}
              onChange={e => setFeat(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (feat.trim()) { setD({ ...d, features: [...d.features, feat.trim()] }); setFeat(""); } } }}
              data-testid="membership-feature-input" />
            <button type="button" className="nb-btn" onClick={() => { if (feat.trim()) { setD({ ...d, features: [...d.features, feat.trim()] }); setFeat(""); } }} data-testid="membership-feature-add"><Plus size={14} /></button>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {d.features.map((x, i) => (
              <span key={`${x}-${i}`} className="nb-badge bg-[#FAF7F0] text-xs">{x}
                <button type="button" onClick={() => setD({ ...d, features: d.features.filter((_, j) => j !== i) })}>×</button></span>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={d.is_active} onChange={e => setD({ ...d, is_active: e.target.checked })} data-testid="membership-active" /> Active</label>
        <div className="flex gap-2 pt-2">
          <button className="nb-btn nb-btn-ghost flex-1 justify-center" onClick={onClose}>Cancel</button>
          <button className="nb-btn flex-1 justify-center" disabled={busy} onClick={save} data-testid="membership-save"><Check size={15} /> Save</button>
        </div>
      </div>
    </div>
  );
}

export default function MembershipManager({ category, accent = "#93C5FD", note }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/membership/admin/plans/${category}`).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [category]);

  const toggleActive = async (p) => {
    await api.patch(`/membership/admin/plans/${category}/${p.id}`, { is_active: !p.is_active });
    load();
  };
  const del = async (p) => { if (!window.confirm(`Delete "${p.name}"?`)) return; await api.delete(`/membership/admin/plans/${category}/${p.id}`); load(); };

  return (
    <div className="space-y-4" data-testid={`membership-manager-${category}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {note && <p className="text-xs opacity-70 flex-1">{note}</p>}
        <button className="nb-btn ml-auto" onClick={() => setEditing({})} data-testid={`membership-add-${category}`}><Plus size={15} /> Add Plan</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map(p => (
          <div key={p.id} className={`nb-card p-4 border-2 border-black ${p.is_active ? "bg-white" : "bg-slate-100 opacity-70"}`} data-testid={`membership-card-${p.id}`} style={{ boxShadow: `4px 4px 0 0 ${accent}` }}>
            <div className="flex justify-between items-start gap-1">
              <h3 className="display text-lg font-bold">{p.name}</h3>
              <div className="flex gap-1">
                <button className="nb-btn nb-btn-secondary px-2 py-1 text-xs" onClick={() => setEditing(p)} data-testid={`membership-edit-${p.id}`}><Pencil size={12} /></button>
                <button className="nb-btn nb-btn-danger px-2 py-1" onClick={() => del(p)}><Trash2 size={12} /></button>
              </div>
            </div>
            <div className="display text-2xl font-black mt-1">
              {CUR[p.currency] || ""}{Number(p.effective_price ?? p.base_price).toLocaleString()}
              <span className="text-xs font-normal">/{p.billing_interval === "monthly" ? "mo" : "yr"}</span>
            </div>
            {p.discount_type !== "none" && p.discount_value > 0 &&
              <div className="text-xs text-green-700 font-bold">{p.discount_type === "percent" ? `${p.discount_value}% off` : `${CUR[p.currency]}${p.discount_value} off`} (was {CUR[p.currency]}{Number(p.base_price).toLocaleString()})</div>}
            <div className="flex flex-wrap gap-1 mt-1">
              {p.trial_days > 0 && <span className="nb-badge bg-[#FDE047] text-[10px]">{p.trial_days}-day trial</span>}
              {p.commission_pct != null && <span className="nb-badge bg-[#FCA5A5] text-[10px]">{p.commission_pct}% take-rate</span>}
            </div>
            <ul className="text-xs mt-2 space-y-1">{(p.features || []).map((x, i) => <li key={i}>✓ {x}</li>)}</ul>
            <button className="text-xs mt-3 underline" onClick={() => toggleActive(p)} data-testid={`membership-toggle-${p.id}`}>
              {p.is_active ? "Deactivate" : "Activate"}
            </button>
          </div>
        ))}
        {!rows.length && <div className="text-sm opacity-60 col-span-full py-6 text-center">No plans yet — add one.</div>}
      </div>
      {editing && <PlanEditor category={category} plan={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
